<template lang="pug">
    .games(:class="{'browse-game': inBrowseGame, 'trainer-setting': inTrainer}")
        template(v-for="(game, index) in games")
            .game
                .game__actions
                    .game__info
                        img(:src="imageSrcString(game.slug)")
                        div
                            .game__title {{ $i18n.t('trainer.trainer_gameList_game') }} {{ index + 1 }}
                            .game__subtitle {{ game.name }}
                    .game__buttons
                        template(v-if="!withoutMoves")
                            a.game__button(
                                v-if="!inTrainer"
                                href="javascript://"
                                @click="$emit('up', index)",
                            )
                                svg-icon(name="circle-up", width="2em", height="2em")
                            a.game__button(
                                v-if="!inTrainer"
                                href="javascript://"
                                @click="$emit('down', index)",
                            )
                                svg-icon(name="circle-down", width="2em", height="2em")
                        a.game__button.game__button_remove(
                            href="javascript://"
                            @click="$emit('remove', index)"
                        )
                            svg-icon(name="circle-close", width="2em", height="2em")
                .game__body
                    .game__control
                        .game__parameters
                            div(
                                v-if="game.parameters.default",
                                :is="getPreview(game.slug)",
                                :game="game"
                                :userId="userId"
                                :withIcon="withAdditionalElements"
                            )
                                template(v-if="individualParameters(game).length > 0")
                                    p
                                        strong {{ $i18n.t('trainer.trainer_gameList_individualParameters') }}:
                                    p(v-for="user in individualParameters(game)") {{user}}
                            cs-button.edit-parameters-button(
                                v-if="isSettingsButtonAvailable(game)"
                                pattern="primary-outline",
                                @click="$emit('editGame', index)",
                                type="button",
                            )
                                span.game__config-button-text {{ $i18n.t('trainer.trainer_gameList_tune') }}
            divider(v-if="withAdditionalElements" :margin="'10px auto'")
</template>

<script>
    const AddSubPreview = () => import('./games-previews/AddSubPreview.vue');
    const FlashCardsPreview = () => import('./games-previews/FlashCardsPreview.vue');
    const MultDivPreview = () => import('./games-previews/MultDivPreview.vue');
    const TabularMultiplicationPreview = () => import('./games-previews/TabularMultiplicationPreview.vue');
    const SequenceOfNumbersPreview = () => import('./games-previews/SequenceOfNumbersPreview.vue');
    const AssembleTheNumberPreview = () => import('./games-previews/AssembleTheNumberPreview.vue');
    const EquationPreview = () => import('./games-previews/EquationPreview.vue');
    const FactorizationPreview = () => import('./games-previews/FactorizationPreview.vue');
    const EquationMultiplicationPreview = () => import('./games-previews/EquationMultiplicationPreview.vue');
    const AnzanPreview = () => import('./games-previews/AnzanPreview.vue');
    const PuzzlePreview = () => import('./games-previews/PuzzlePreview.vue');
    const CouplesPreview = () => import('./games-previews/CouplesPreview.vue');
    const ChainsPreview = () => import('./games-previews/ChainsPreview.vue');
    const ScannerPreview = () => import('./games-previews/ScannerPreview.vue');
    const ShutterPreview = () => import('./games-previews/ShutterPreview.vue');
    const ShutterReadingEnPreview = () => import('./games-previews/ShutterReadingEnPreview.vue');
    const ShutterRussianLanguagePreview = () => import('./games-previews/ShutterRussianLanguagePreview.vue');
    const WordsFieldPreview = () => import('./games-previews/WordsFieldPreview.vue');
    const WordsFieldReadingEnPreview = () => import('./games-previews/WordsFieldReadingEnPreview.vue');
    const AnagramsPreview = () => import('./games-previews/AnagramsPreview.vue');
    const TextsPreview = () => import('./games-previews/TextsPreview.vue');
    const LettersFieldPreview = () => import('./games-previews/LettersFieldPreview.vue');
    const CatchWordPreview = () => import('./games-previews/CatchWordPreview.vue');
    const CatchWordReadingEnPreview = () => import('./games-previews/CatchWordReadingEnPreview.vue');
    const VisionFieldPreview = () => import('./games-previews/VisionFieldPreview.vue');
    const VisionFieldReadingEnPreview = () => import('./games-previews/VisionFieldReadingEnPreview.vue');
    const SchultePreview = () => import('./games-previews/SchultePreview.vue');
    const MatrixPreview = () => import('./games-previews/MatrixPreview.vue');
    const MemorizePreview = () => import('./games-previews/MemorizePreview');
    const KeenEyePreview = () => import('./games-previews/KeenEyePreview');
    const NumericalNetworkPreview = () => import('./games-previews/NumericalNetworkPreview');
    const NavigatorPreview = () => import('./games-previews/NavigatorPreview.vue');
    const SynonymsPreview = () => import('./games-previews/SynonymsPreview.vue');
    const SynonymsReadingEnPreview = () => import('./games-previews/SynonymsReadingEnPreview.vue');
    const SynonymsRussianLanguagePreview = () => import('./games-previews/SynonymsRussianLanguagePreview.vue');
    const AntonymsPreview = () => import('./games-previews/AntonymsPreview.vue');
    const ClassificationsPreview = () => import('./games-previews/Classifications.vue');
    const SoundCouplesPreview = () => import('./games-previews/SoundCouples.vue');
    const FourthOnePreview = () => import('./games-previews/FourthOne.vue');
    const SearchPreview = () => import('./games-previews/Search.vue');
    const EquationOnlinePreview = () => import('./games-previews/EquationOnlinePreview.vue');
    const DigitCouplesPreview = () => import('./games-previews/DigitCouplesPreview.vue');
    const MemoryPreview = () => import('./games-previews/MemoryPreview.vue');
    const ExpressionsPreview = () => import('./games-previews/ExpressionsPreview.vue');
    const RootsAndDegreesPreview = () => import('./games-previews/RootsAndDegreesPreview');
    const RootsAndSquaresPreview = () => import('./games-previews/RootsAndSquaresPreview');
    const DayOfWeekPreview = () => import('./games-previews/DayOfWeekPreview');
    const LonerPreview = () => import('./games-previews/LonerPreview');
    const LonerBonusGamesPreview = () => import('./games-previews/LonerBonusGamesPreview');
    const MemoPreview = () => import('./games-previews/MemoPreview');
    const MemoryXPreview = () => import('./games-previews/MemoryXPreview.vue');
    const RevivedNumbersPreview = () => import('./games-previews/RevivedNumbersPreview');
    const AssociationsPreview = () => import('./games-previews/AssociationsPreview');
    const WhoIsWhoPreview = () => import('./games-previews/WhoIsWhoPreview');
    const SecretsOfCiceroPreview = () => import('./games-previews/SecretsOfCiceroPreview');
    const VocabularyWordsPreview = () => import('./games-previews/VocabularyWordsPreview.vue');
    const VocabularyMemoPreview = () => import('./games-previews/VocabularyMemoPreview.vue');
    const CosmoTourPreview = () => import('./games-previews/CosmoTourPreview');
    const ChameleonPreview = () => import('./games-previews/ChameleonPreview');
    const MentalDrawingPreview = () => import('./games-previews/MentalDrawingPreview');
    const HandsUpPreview = () => import('./games-previews/HandsUpPreview');
    const RepeatAfterMePreview = () => import('./games-previews/RepeatAfterMePreview');
    const CorrectiveSamplesPreview = () => import('./games-previews/CorrectiveSamplesPreview');
    const ChainsBonusGamePreview = () => import('./games-previews/ChainsBonusGamePreview');
    const ScoutPreview = () => import('./games-previews/ScoutPreview');
    const DiadaPreview = () => import('./games-previews/DiadaPreview');
    const AlpariPreview = () => import('./games-previews/AlpariPreview.vue');
    const ChainsReadingEnPreview = () => import('./games-previews/ChainsReadingEnPreview.vue');
    const CouplesReadingEnPreview = () => import('./games-previews/CouplesReadingEnPreview.vue');
    const DivideItRightPreview = () => import('./games-previews/DivideItRightPreview.vue');
    const ChooseItRightPreview = () => import('./games-previews/ChooseItRightPreview.vue');
    const DivideUnstressedVowelsPreview = () => import('./games-previews/DivideUnstressedVowelsPreview.vue');
    const WordStructurePreview = () => import('./games-previews/WordStructurePreview.vue');
    const SentenceParsingPreview = () => import('./games-previews/SentenceParsingPreview.vue');
    const VocabularyWordsRusPreview = () => import('./games-previews/VocabularyWordsRusPreview.vue');
    const LettersSoundsSyllablesPreview = () => import('./games-previews/LettersSoundsSyllablesPreview.vue');
    const TransferSyllablesPreview = () => import('./games-previews/TransferSyllablesPreview.vue');
    const TransferSyllablesSchoolPreparingPreview = () => import('./games-previews/TransferSyllablesSchoolPreparingPreview.vue');
    const HardSoftConsonantsPreview = () => import('./games-previews/HardSoftConsonantsPreview.vue');
    const HardSoftConsonantsSchoolPreparingPreview = () => import('./games-previews/HardSoftConsonantsSchoolPreparingPreview.vue');
    const WordDrumPreview = () => import('./games-previews/WordDrumPreview.vue');
    const VocabularyMemoRusPreview = () => import('./games-previews/VocabularyMemoRusPreview.vue');
    const TranscriptionInWordPreview = () => import('./games-previews/TranscriptionInWordPreview.vue');
    const VoicedAndDeafConsonantsPreview = () => import('./games-previews/VoicedAndDeafConsonantsPreview.vue');
    const PartsOfSpeechPreview = () => import('./games-previews/PartsOfSpeechPreview.vue');
    const ParsingSentencePartsSpeechPreview = () => import('./games-previews/ParsingSentencePartsSpeechPreview.vue');
    const SortOneManyPreview = () => import('./games-previews/SortOneManyPreview.vue');
    const GenericWordsPreview = () => import('./games-previews/GenericWordsPreview.vue');
    const VerbTensesPreview = () => import('./games-previews/VerbTensesPreview.vue');
    const PunctuationMarksPreview = () => import('./games-previews/PunctuationMarksPreview.vue');
    const AlphabetPreview = () => import('./games-previews/AlphabetPreview.vue');
    const ConnectThePuzzlesPreview = () => import('./games-previews/ConnectThePuzzlesPreview.vue');
    const OrthographPreview = () => import('./games-previews/OrthographPreview.vue');
    const AntonymsRussianLanguagePreview = () => import('./games-previews/AntonymsRussianLanguagePreview.vue');

    import TrainerConfig from '../../config/trainer.json';

    const ImagesBySlug = TrainerConfig.games.reduce((acc, { slug, img }) => ((acc[slug] = img), acc), {});

    const NAMES_OF_GAMES = ['Puzzle'];

    const GAMES_WITHOUT_COURSE_SETTINGS = [
        'VoicedAndDeafConsonants',
        'PartsOfSpeech',
        'ParsingSentencePartsSpeech',
        'SortOneMany',
        'GenericWords',
        'VerbTenses',
        'ConnectThePuzzles',
        'DivideItRight',
        'ChooseItRight',
        'DivideUnstressedVowels',
        'WordStructure',
        'SentenceParsing',
        'VocabularyWordsRus',
        'Alphabet',
        'TranscriptionInWord',
        'TransferSyllables',
        'TransferSyllablesSchoolPreparing',
        'HardSoftConsonants',
        'HardSoftConsonantsSchoolPreparing',
        'WordDrum',
        'VocabularyMemoRus',
        'OrthographPreview',
        'AntonymsRussianLanguage',
        'Factorization',
        'Puzzle',
        'Couples',
        'CouplesMemoryDevelopment',
        'InteractiveFlashCards',
        'FlashCards',
        'Chains',
        'ChainsReadingEn',
        'CouplesReadingEn',
        'Schulte',
        'Scanner',
        'ScannerReadingEn',
        'Shutter',
        'ShutterReadingEn',
        'ShutterRussianLanguage',
        'WordsField',
        'WordsFieldReadingEn',
        'CorrectiveSamples',
        'Anagrams',
        'LettersField',
        'LettersFieldOnline',
        'CatchWord',
        'CatchWordReadingEn',
        'VisionField',
        'VisionFieldReadingEn',
        'Matrix',
        'Navigator',
        'NavigatorBonusGames',
        'Synonyms',
        'SynonymsReadingEn',
        'SynonymsRussianLanguage',
        'SynonymsOnline',
        'Presentations',
        'Antonyms',
        'Search',
        'AnagramsOnline',
        'ShutterOnline',
        'Classifications',
        'CatchWordOnline',
        'SoundCouples',
        'FourthOne',
        'EquationOnline',
        'EquationMultiplication',
        'DigitCouples',
        'Memory',
        'Memo',
        'MemoBonusGames',
        'MemoryX',
        'RevivedNumbers',
        'Associations',
        'WhoIsWho',
        'SecretsOfCicero',
        'VocabularyWords',
        'VocabularyMemo',
        'CosmoTour',
        'Chameleon',
        'MentalDrawing',
        'HandsUp',
        'RepeatAfterMe',
        'ChainsBonusGame',
        'Diada',
        'Alpari',
        'LettersSoundsSyllables',
        'PunctuationMarks',
        'Orthograph',
    ];

    export default {
        props: {
            games: Array,
            students: {
                type: Array,
                default: () => {
                    return [];
                },
            },
            inTrainer: {
                type: Boolean,
                default: false,
            },
            inBrowseGame: {
                type: Boolean,
                default: false,
            },
            withoutMoves: {
                type: Boolean,
                default: false,
            },
            userId: {
                type: Number,
                default: null,
            },
        },
        computed: {
            withAdditionalElements() {
                return !(this.inTrainer || this.inBrowseGame);
            },
        },
        methods: {
            individualParameters(game) {
                let array = [];
                let handleParams = game.parameters;
                if (
                    NAMES_OF_GAMES.includes(game.slug) &&
                    !_.isArray(game.parameters.default) &&
                    game.parameters.default.games[0] &&
                    game.parameters.default.games[0].parameters
                ) {
                    handleParams = game.parameters.default.games[0].parameters;
                    for (let key in handleParams) {
                        if (key !== 'default') {
                            this.students.forEach(student => {
                                if (key.match(/[\d]*[\d]+/g).some(userId => +userId === student.id)) {
                                    array.push(student.fullName);
                                }
                            });
                        }
                    }
                }
                if (
                    NAMES_OF_GAMES.includes(game.slug) &&
                    _.isArray(game.parameters.default) &&
                    game.parameters.default.every(param => param.games[0]) &&
                    game.parameters.default.every(param => param.games[0].parameters)
                ) {
                    handleParams = game.parameters.default
                        .map(defaultParams => {
                            return defaultParams.games.params;
                        })
                        .join('');
                }
                return array;
            },
            getPreview(game) {
                switch (game) {
                    case 'MultiplicationAndDivision':
                    case 'MultiplicationAndDivisionMathMagic':
                        return 'mult-div-preview';
                    case 'TabularMultiplication':
                        return 'tabular-multiplication-preview';
                    case 'SequenceOfNumbers':
                        return 'sequence-of-numbers-preview';
                    case 'AssembleTheNumber':
                        return 'assemble-the-number-preview';
                    case 'FlashCards':
                    case 'InteractiveFlashCards':
                        return 'flash-cards-preview';
                    case 'AdditionAndSubtraction':
                    case 'AdditionAndSubtractionMathMagic':
                    case 'AdditionAndSubtractionWithTime':
                        return 'add-sub-preview';
                    case 'Equation':
                        return 'equation-preview';
                    case 'Factorization':
                        return 'factorization-preview';
                    case 'EquationMultiplication':
                        return 'equation-multiplication-preview';
                    case 'Anzan':
                        return 'anzan-preview';
                    case 'Puzzle':
                        return 'puzzle-preview';
                    case 'CouplesMemoryDevelopment':
                    case 'Couples':
                        return 'couples-preview';
                    case 'Chains':
                    case 'ChainsMemoryDevelopment':
                        return 'chains-preview';
                    case 'Scanner':
                    case 'ScannerReadingEn':
                        return 'scanner-preview';
                    case 'Expressions':
                        return 'expressions-preview';
                    case 'Shutter':
                    case 'ShutterOnline':
                        return 'shutter-preview';
                    case 'WordsField':
                    case 'WordsFieldOnline':
                    case 'WordsFieldOnlineWithTime':
                        return 'words-field-preview';
                    case 'WordsFieldReadingEn':
                    case 'WordsFieldReadingEnWithTime':
                        return 'words-field-reading-en-preview';
                    case 'Anagrams':
                    case 'AnagramsOnline':
                        return 'anagrams-preview';
                    case 'Texts':
                    case 'TextsOnline':
                        return 'texts-preview';
                    case 'RootsAndDegrees':
                        return 'roots-and-degrees-preview';
                    case 'RootsAndSquares':
                        return 'roots-and-squares-preview';
                    case 'LettersField':
                    case 'LettersFieldOnline':
                    case 'LettersFieldReadingEn':
                        return 'letters-field-preview';
                    case 'CatchWord':
                    case 'CatchWordOnline':
                        return 'catch-word-preview';
                    case 'VisionField':
                        return 'vision-field-preview';
                    case 'Schulte':
                    case 'SchulteReadingEn':
                        return 'schulte-preview';
                    case 'Matrix':
                        return 'matrix-preview';
                    case 'Navigator':
                    case 'NavigatorMemoryDevelopment':
                    case 'NavigatorBonusGames':
                        return 'navigator-preview';
                    case 'Synonyms':
                    case 'SynonymsOnline':
                        return 'synonyms-preview';
                    case 'Antonyms':
                        return 'antonyms-preview';
                    case 'Classifications':
                        return 'classifications-preview';
                    case 'SoundCouples':
                        return 'sound-couples-preview';
                    case 'FourthOne':
                        return 'fourth-one-preview';
                    case 'Search':
                        return 'search-preview';
                    case 'EquationOnline':
                        return 'equation-online-preview';
                    case 'DigitCouples':
                        return 'digit-couples-preview';
                    case 'Memory':
                        return 'memory-preview';
                    case 'DayOfWeek':
                        return 'day-of-week-preview';
                    case 'Memorize':
                        return 'memorize-preview';
                    case 'KeenEye':
                    case 'KeenEyeMemoryDevelopment':
                    case 'KeenEyeBonusGames':
                        return 'keen-eye-preview';
                    case 'NumericalNetwork':
                        return 'numerical-network-preview';
                    case 'Loner':
                    case 'LonerMemoryDevelopment':
                        return 'loner-preview';
                    case 'LonerBonusGames':
                        return 'loner-bonus-games-preview';
                    case 'Memo':
                    case 'MemoBonusGames':
                        return 'memo-preview';
                    case 'MemoryX':
                        return 'memory-x-preview';
                    case 'RevivedNumbers':
                        return 'revived-numbers-preview';
                    case 'Associations':
                        return 'associations-preview';
                    case 'WhoIsWho':
                        return 'who-is-who-preview';
                    case 'SecretsOfCicero':
                        return 'secrets-of-cicero-preview';
                    case 'VocabularyWords':
                        return 'vocabulary-words-preview';
                    case 'VocabularyMemo':
                        return 'vocabulary-memo-preview';
                    case 'CosmoTour':
                        return 'cosmo-tour-preview';
                    case 'Chameleon':
                        return 'chameleon-preview';
                    case 'MentalDrawing':
                        return 'mental-drawing-preview';
                    case 'HandsUp':
                        return 'hands-up-preview';
                    case 'RepeatAfterMe':
                        return 'repeat-after-me-preview';
                    case 'CorrectiveSamples':
                    case 'CorrectiveSamplesWithTime':
                        return 'corrective-samples-preview';
                    case 'ChainsBonusGame':
                    case 'ChainsBonusGameWithTime':
                    case 'ChainsBonusGameWithoutTime':
                        return 'chains-bonus-game-preview';
                    case 'Scout':
                        return 'scout-preview';
                    case 'Diada':
                        return 'diada-preview';
                    case 'Alpari':
                        return 'alpari-preview';
                    case 'ChainsReadingEn':
                    case 'ChainsReadingEnWithTime':
                    case 'ChainsReadingEnWithoutTime':
                        return 'chains-reading-en-preview';
                    case 'CouplesReadingEn':
                    case 'CouplesReadingEnWithTime':
                    case 'CouplesReadingEnWithoutTime':
                        return 'couples-reading-en-preview';
                    case 'CatchWordReadingEn':
                        return 'catch-word-reading-en-preview';
                    case 'VisionFieldReadingEn':
                        return 'vision-field-reading-en-preview';
                    case 'ShutterReadingEn':
                        return 'shutter-reading-en-preview';
                    case 'ShutterRussianLanguage':
                        return 'shutter-russian-language-preview';
                    case 'SynonymsReadingEn':
                        return 'synonyms-reading-en-preview';
                    case 'SynonymsRussianLanguage':
                        return 'synonyms-russian-language-preview';
                    case 'DivideItRight':
                        return 'divide-it-right-preview';
                    case 'ChooseItRight':
                        return 'choose-it-right-preview';
                    case 'DivideUnstressedVowels':
                        return 'divide-unstressed-vowels-preview';
                    case 'WordStructure':
                        return 'word-structure-preview';
                    case 'SentenceParsing':
                        return 'sentence-parsing-preview';
                    case 'VocabularyWordsRus':
                        return 'vocabulary-words-rus-preview';
                    case 'LettersSoundsSyllables':
                        return 'letters-sounds-syllables-preview';
                    case 'TransferSyllables':
                        return 'transfer-syllables-preview';
                    case 'TransferSyllablesSchoolPreparing':
                        return 'transfer-syllables-school-preparing-preview';
                    case 'HardSoftConsonants':
                        return 'hard-soft-consonants-preview';
                    case 'HardSoftConsonantsSchoolPreparing':
                        return 'hard-soft-consonants-school-preparing-preview';
                    case 'WordDrum':
                        return 'word-drum-preview';
                    case 'VocabularyMemoRus':
                        return 'vocabulary-memo-rus-preview';
                    case 'TranscriptionInWord':
                        return 'transcription-in-word-preview';
                    case 'VoicedAndDeafConsonants':
                        return 'voiced-and-deaf-consonants-preview';
                    case 'PartsOfSpeech':
                        return 'parts-of-speech-preview';
                    case 'ParsingSentencePartsSpeech':
                        return 'parsing-sentence-parts-speech-preview';
                    case 'SortOneMany':
                        return 'sort-one-many-preview';
                    case 'GenericWords':
                        return 'generic-words-preview';
                    case 'VerbTenses':
                        return 'verb-tenses-preview';
                    case 'PunctuationMarks':
                        return 'punctuation-marks-preview';
                    case 'Alphabet':
                        return 'alphabet-preview';
                    case 'ConnectThePuzzles':
                        return 'connect-the-puzzles-preview';
                    case 'Orthograph':
                        return 'orthograph-preview';
                    case 'AntonymsRussianLanguage':
                        return 'antonyms-russian-language-preview';
                }
            },
            imageSrcString(slug) {
                return ImagesBySlug[slug];
            },
            isGameWithCourseSettings(game) {
                return (
                    this.$route.name === 'homework:create' ||
                    this.$route.name === 'homework:edit' ||
                    this.$route.name === 'homework-template:create' ||
                    this.$route.name === 'homework-template:edit' ||
                    !GAMES_WITHOUT_COURSE_SETTINGS.includes(game.slug)
                );
            },
            isSettingsButtonAvailable(game) {
                return this.$route.name === 'course:create' || this.$route.name === 'course:edit'
                    ? this.isGameWithCourseSettings(game)
                    : true;
            },
        },
        components: {
            AddSubPreview,
            FlashCardsPreview,
            MultDivPreview,
            TabularMultiplicationPreview,
            SequenceOfNumbersPreview,
            AssembleTheNumberPreview,
            EquationPreview,
            FactorizationPreview,
            EquationMultiplicationPreview,
            AnzanPreview,
            PuzzlePreview,
            CouplesPreview,
            ChainsPreview,
            ScannerPreview,
            ExpressionsPreview,
            ShutterPreview,
            ShutterReadingEnPreview,
            ShutterRussianLanguagePreview,
            WordsFieldPreview,
            WordsFieldReadingEnPreview,
            AnagramsPreview,
            TextsPreview,
            LettersFieldPreview,
            CatchWordPreview,
            CatchWordReadingEnPreview,
            VisionFieldPreview,
            VisionFieldReadingEnPreview,
            SchultePreview,
            ConnectThePuzzlesPreview,
            MatrixPreview,
            MemorizePreview,
            KeenEyePreview,
            NumericalNetworkPreview,
            NavigatorPreview,
            SynonymsPreview,
            SynonymsReadingEnPreview,
            SynonymsRussianLanguagePreview,
            AntonymsPreview,
            ClassificationsPreview,
            SoundCouplesPreview,
            FourthOnePreview,
            SearchPreview,
            EquationOnlinePreview,
            DigitCouplesPreview,
            MemoryPreview,
            RootsAndDegreesPreview,
            RootsAndSquaresPreview,
            DayOfWeekPreview,
            LonerPreview,
            LonerBonusGamesPreview,
            MemoPreview,
            MemoryXPreview,
            RevivedNumbersPreview,
            AssociationsPreview,
            WhoIsWhoPreview,
            SecretsOfCiceroPreview,
            VocabularyWordsPreview,
            VocabularyMemoPreview,
            CosmoTourPreview,
            ChameleonPreview,
            MentalDrawingPreview,
            HandsUpPreview,
            RepeatAfterMePreview,
            CorrectiveSamplesPreview,
            ChainsBonusGamePreview,
            ScoutPreview,
            DiadaPreview,
            AlpariPreview,
            ChainsReadingEnPreview,
            CouplesReadingEnPreview,
            TransferSyllablesPreview,
            TransferSyllablesSchoolPreparingPreview,
            TranscriptionInWordPreview,
            AlphabetPreview,
            DivideItRightPreview,
            ChooseItRightPreview,
            DivideUnstressedVowelsPreview,
            WordStructurePreview,
            SentenceParsingPreview,
            VocabularyWordsRusPreview,
            LettersSoundsSyllablesPreview,
            HardSoftConsonantsPreview,
            HardSoftConsonantsSchoolPreparingPreview,
            WordDrumPreview,
            VocabularyMemoRusPreview,
            VoicedAndDeafConsonantsPreview,
            PartsOfSpeechPreview,
            ParsingSentencePartsSpeechPreview,
            GenericWordsPreview,
            VerbTensesPreview,
            SortOneManyPreview,
            PunctuationMarksPreview,
            OrthographPreview,
            AntonymsRussianLanguagePreview,
        },
    };
</script>

<style lang="scss" scoped>
    .game {
        margin-top: 30px;
    }
    .game__info {
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        @media screen and (max-width: 575px) {
            display: block;
        }
    }
    .game__actions {
        position: relative;
    }
    .game__buttons {
        display: flex;
        white-space: nowrap;
        position: absolute;
        right: 0;
        top: 0;
    }
    .game__button {
        color: $hsl-color-green-48;
        border-radius: $border-radius-circle;
        &:hover {
            color: $x-color-white;
            background-color: $hsl-color-green-48;
        }
        display: flex;
        margin: 0 5px;
    }
    .game__button_remove {
        margin-left: 20px;
    }
    .game__title,
    .game__subtitle {
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        margin-left: 5px;
    }
    .game__title {
        @media screen and (max-width: 575px) {
            margin: 20px 0 0 0;
        }
    }
    .game__subtitle {
        @media screen and (max-width: 575px) {
            margin: 0 0 25px 0;
        }
    }
    .game__parameters {
        font-size: 16px;
        line-height: 24px;
        display: flex;
        justify-content: space-between;
        .edit-parameters-button {
            max-height: 50px;
            max-width: 170px;
            @media screen and (max-width: 575px) {
                margin: 25px 0 30px 0;
            }
        }
        @media screen and (max-width: 575px) {
            flex-direction: column;
        }
    }
    .browse-game {
        display: flex;
        justify-content: center;
        .game {
            margin-top: 0;
            &__button {
                display: none;
            }
            &__tune {
                display: none;
            }
        }
        .edit-parameters-button {
            display: none;
        }
    }
    .trainer-setting {
        .game {
            &__control {
                margin: 0;
            }
        }
        .game__parameters {
            flex-direction: column;
            .edit-parameters-button {
                margin: 25px 0 30px 0;
            }
        }
    }
    .browse-game,
    .trainer-setting {
        .game {
            &__title {
                display: none;
            }
            &__subtitle {
                display: none;
            }
        }
    }
</style>
